import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { doc, updateDoc } from 'firebase/firestore'
import { useState } from 'react'
import BasicDateStringPicker from '../BasicDateStringPicker'
import moment from 'moment'
import isRawDateValid from '../../utils/isRawDateValid'
import { Regimen } from '../../types/Regimen'
import { getEffectiveServices } from '../../utils/firebaseServiceContext'

interface EndRegimenModalProps {
  isOpen: boolean
  onClose: () => void
  confirmEndId: string | undefined
  records: Regimen[]
  recordLabel: string
}

export default function EndRegimenModal({
  isOpen,
  onClose,
  confirmEndId,
  records,
  recordLabel,
}: EndRegimenModalProps) {
  const [endDate, setEndDate] = useState('')
  const toast = useToast()

  const handleClose = () => {
    setEndDate('')
    onClose()
  }

  const handleEndRegimen = () => {
    if (!isRawDateValid(endDate)) {
      return toast({
        status: 'error',
        title: 'Error',
        description:
          'You have entered an invalid End Date. Please enter in the format YYYY-MM-DD.',
        isClosable: true,
      })
    }

    const formattedEndDate = moment(endDate, 'YYYY-MM-DD')
    const currentMonthEnd = moment().endOf('month')

    if (formattedEndDate.isAfter(currentMonthEnd)) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'End date must be in or before the current month.',
      })
    }

    const recordToEnd = records.find((r) => r.id === confirmEndId)
    if (!recordToEnd) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'Could not find record to end.',
      })
    }

    const lastUsageStartDate = moment(
      recordToEnd.usages[recordToEnd.usages.length - 1].startDate,
      'YYYY-MM-DD'
    )

    if (formattedEndDate.isBefore(lastUsageStartDate)) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'End date must be after the start date.',
      })
    }

    updateDoc(doc(getEffectiveServices().firestore, `regimens/${confirmEndId}`), {
      endDate,
    })

    handleClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Stop {recordLabel}</ModalHeader>
        <ModalBody>
          <Stack>
            <Text>On what date did you stop? (YYYY-MM-DD)</Text>
            <FormControl>
              <FormLabel>End Date</FormLabel>
              <BasicDateStringPicker
                startYear={'1920'}
                endYear={new Date().getFullYear().toString()}
                dateString={endDate}
                setDateString={setEndDate}
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" onClick={handleClose} mr={3}>
            Cancel
          </Button>
          <Button
            isDisabled={!endDate}
            colorScheme="red"
            onClick={handleEndRegimen}
          >
            Stop
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
